/**
 * Small shared UX helpers used across Home / Today / Progress / Report.
 * These centralize "human copy" and score-band colors so parents get a
 * consistent read of a stat wherever it appears.
 */

/* ─────────────  Time-of-day / progress-of-day copy  ─────────────
 * Feeds Today's Plan header. Fixes the "0/6 completed · Keep going!"
 * problem — which sounds harsh in the morning — by picking a phrase
 * that fits both the time and how much has been done.
 */
export function timeOfDayCopy(completed = 0, total = 0) {
  const hour = new Date().getHours();
  const pct = total > 0 ? completed / total : 0;

  if (total > 0 && completed >= total) {
    return "All done — nicely done!";
  }
  if (pct >= 0.5) return "Halfway there — keep it going!";
  if (completed > 0) return "Great start — one at a time.";
  // Nothing completed yet — pick something friendly for the time of day.
  if (hour < 5)  return "Late night? No pressure — pick this up tomorrow.";
  if (hour < 12) return "Fresh start · plan ready for the morning.";
  if (hour < 17) return "Ready when you are — this afternoon works too.";
  if (hour < 21) return "Evening plan · easy activities to wind down.";
  return "Wind down · just one activity is a win.";
}

/* ────────────────────  Score bands (traffic-light)  ────────────────────
 * Kiddo+ uses three developmental bands: "On track" (green),
 * "Emerging" (amber), "Needs support" (red). We colour every progress
 * bar the same way so parents can spot concerns at a glance without
 * decoding numbers.
 */
export function scoreBand(percentage) {
  const p = Math.max(0, Math.min(100, Number(percentage) || 0));
  if (p >= 75) return { key: "on-track", label: "On track",     tone: "green", color: "#22C55E", soft: "rgba(34,197,94,0.15)" };
  if (p >= 34) return { key: "emerging", label: "Emerging",     tone: "amber", color: "#F59E0B", soft: "rgba(245,158,11,0.15)" };
  return              { key: "support",  label: "Needs support", tone: "red",   color: "#EF4444", soft: "rgba(239,68,68,0.15)" };
}

/* Short caption for a stat tile so numbers never appear naked.
 *   describeDelta({ timeframe: "this week", direction: "up" })
 *   → "this week"
 * If a timeframe isn't provided we fall back to a sensible default
 * per stat kind ("since first assessment", etc.).
 */
export const STAT_CAPTIONS = {
  overallImprovement: "since first assessment",
  skillsPracticed:   "logged this month",
  activitiesCompleted: "this month",
  daysConsistent:    "consecutive days",
  streak:            "day streak",
};
